/**
 * ContentPlansPage — "Content Plans" tab inside ProjectView.
 *
 * v2 layout — 3 sub-tabs for the staged pipeline:
 *   • Backlog (default) — topic_backlog/ list + Generate-topics dialog (Stage A)
 *   • Creators — creator_references/ + Refresh (Stage D)
 *   • Strategies — drill-down Strategies → Sprints → Sprint detail (Stage B + C)
 *
 * URL-driven state:
 *   ?cpTab=backlog|creators|strategies (default backlog)
 *   ?strategy={id}&sprint={id}  (inside Strategies)
 */

import React, { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box, Stack, Typography, Button,
  Alert, Chip, Tabs, Tab, IconButton, MenuItem, Tooltip,
  Paper, Divider, TextField, Badge,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import DeleteIcon from '@mui/icons-material/Delete';
import HistoryIcon from '@mui/icons-material/History';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import LanguageIcon from '@mui/icons-material/Language';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PaletteIcon from '@mui/icons-material/Palette';
import PersonIcon from '@mui/icons-material/Person';
import RouteIcon from '@mui/icons-material/Route';
import ForumOutlinedIcon from '@mui/icons-material/ForumOutlined';
import type { SvgIconComponent } from '@mui/icons-material';
import {
  DynamicTable, type TableColumn,
  SDialog,
  BaseDialog,
  DialogHeader,
  DialogContent,
  DialogFooter,
} from '@modules/ui-components';
import {
  SDataTable,
  type SDataTableColumnDef,
  type SDataTableRowSelectionState,
} from '@modules/ui-components/dialogs/SDataTable';
import { AvailableModelSelector } from '@modules/ai-settings';
import { TERMINAL_STATUSES, registerTaskResolveHandler, useTaskActions, useTasks } from '@modules/background-tasks';
import { topicBacklogFirestoreService } from '../services/TopicBacklogFirestoreService';

import { CreateContentStrategyDialog } from './CreateContentStrategyDialog';
import { GenerateTopicsDialog } from './GenerateTopicsDialog';
import { ManualTopicDialog } from './topic/ManualTopicDialog';
import { ThemesTab } from './ThemesTab';
import { TopicDetailPanel } from './TopicDetailPanel';
import { PlatformsSection } from './platforms/PlatformsSection';
// Concurrent Minsky refactor — PostExamplesTab and tracked Creator Research
// are hidden from primary navigation. Platform setup now lives in its own
// shallow Platforms sub-tab.
import { ContentCalendarPanel } from './calendar';
import { SocialEngagementPanel } from './engagement/SocialEngagementPanel';
import { GenerationHistoryList } from './generations/GenerationHistoryList';
import { GenerationReviewDialog } from './generations/GenerationReviewDialog';
import { useGenerationRuns, type GenerationRunSummary } from '../hooks/useGenerationRuns';
import { StrategyService } from '@modules/firebase-core/services/StrategyService';

import { useContentStrategy } from '../hooks/useContentStrategy';
import { useTopicBacklog } from '../hooks/useTopicBacklog';
import { usePillars } from '../hooks/usePillars';
import { useSprintPosts, useUpdateSprintPost } from '../hooks/useSprintPosts';
import { useProjectDefaultModel } from '../hooks/useProjectDefaultModel';
// Concurrent Minsky refactor — useProjectPostExamples retired (collection
// `post_examples/*` merged into `author_posts/*` with curation tags).
import { ContentStrategyFirestoreService } from '../services/ContentStrategyFirestoreService';
import { ContentPlanApiClient } from '../services/ContentPlanApiClient';
import type {
  CalendarAwareContentStrategy,
  ContentStrategy,
  ContentSprint,
  ContentTopic,
  ContentPost,
  ContentPublication,
  Post,
  PlatformId,
  StrategyPlatformCadence,
} from '../types';
import { parseDrillDown, applyDrillDownState, getTopicRoutePath, type DrillDownState } from '../utils/urlNavigation';
import { CADENCE_DAY_CODES, postsPerWeekFromCadence } from '../utils/strategyCadence';
import { UnifiedPostCell } from './postLike/UnifiedPostCell';
import { UnifiedPostDialog } from './postLike/UnifiedPostDialog';
import { AuthorOnboardingDialog, type AudienceOption } from './author/AuthorOnboardingDialog';
import { AuthorOnboardingStatusBanner } from './author/AuthorOnboardingStatusBanner';
import { CreatorTab } from './author/CreatorTab';
import { AuthorPickerScreen } from './author/AuthorPickerScreen';
import { AuthorSelector } from './author/AuthorSelector';
import { CloneAuthorDialog } from './author/CloneAuthorDialog';
import { useAuthorWorkspace } from '../hooks/useAuthor';
import {
  cascadeDeleteSelfAuthor,
  previewCascadeDeleteSelfAuthor,
  type CascadeDeleteCounts,
} from '../services/authorCascadeDelete';
import type { AuthorDoc } from '../services/AuthorFirestoreService';
import {
  contentPostToPostLike,
  postLikeToContentPostPatch,
  type PostLike,
} from '../utils/postLike';
import { StorageService } from '@modules/firebase-core/services/StorageService';
import { ContentPlanWizardStrip } from './wizard/ContentPlanWizardStrip';
import type { WizardSubTabId } from './wizard/wizardSteps';

/**
 * G5 (AC-GP-029): the task types this page registers a resolve handler for while
 * it is mounted — `TaskErrorResolver` offers a generic action only for these.
 * Deliberately narrow: `content-strategy` is where `INSUFFICIENT_BACKLOG` /
 * `EMPTY_SOURCE_DATA` come from, and `content-post-generation` keeps the Retry it
 * has today. A type nobody here can act on must NOT be listed — that is exactly
 * the dead button this task removes.
 */
const CONTENT_PLAN_RESOLVABLE_TASK_TYPES = ['content-strategy', 'content-post-generation'] as const;

// ─── testid string literals (mirrored in @test-kit/selectors/content-plan) ──
const CONTENT_PLAN = {
  PAGE_ROOT: 'content-plans-page',
  SUBTAB_BACKLOG: 'cp-subtab-backlog',
  SUBTAB_THEMES: 'cp-subtab-themes',
  // Concurrent Minsky refactor — SUBTAB_POST_EXAMPLES retired with the sub-tab.
  SUBTAB_PLATFORMS: 'content-plan-subtab-platforms',
  SUBTAB_STRATEGIES: 'cp-subtab-strategies',
  SUBTAB_CALENDAR: 'cp-subtab-calendar',
  SUBTAB_ENGAGEMENT: 'cp-subtab-engagement',
  OPEN_GEN_TOPICS: 'gen-topics-open-btn',
  TOPIC_GENERATION_HISTORY_BUTTON: 'topic-generation-history-button',
  TOPIC_GENERATION_HISTORY_DIALOG: 'topic-generation-history-dialog',
  OPEN_DIALOG_BTN: 'create-content-strategy-open-btn',
  STRATEGIES_TABLE: 'content-strategies-table',
  STATUS_BADGE: 'content-strategy-status',
  STRATEGY_DETAIL_DRAWER: 'strategy-detail-drawer',
  SPRINTS_TABLE: 'sprints-table',
  SPRINT_DETAIL_DIALOG: 'sprint-detail-dialog',
  TOPICS_TABLE: 'sprint-topics-table',
  BACKLOG_TABLE: 'topic-backlog-table',
  BREADCRUMB_ROOT: 'cp-breadcrumb-root',
  GENERATE_STRATEGY_POSTS: 'strategy-generate-all-posts',
  GENERATE_SPRINT_POSTS: 'sprint-generate-posts',
  // M2 wizard + dead-end CTAs (S3.2/S3.3)
  TOAST_VIEW_PROGRESS: 'toast-action-view-progress',
  STRATEGY_ASSIGN_TOPICS_CTA: 'strategy-assign-topics-cta',
  SPRINT_GENERATE_TOPICS_CTA: 'sprint-generate-topics-cta',
  OVERVIEW_PICK_AUTHOR_CTA: 'overview-pick-author-cta',
  OVERVIEW_AUTHOR_PICKER_DIALOG: 'overview-author-picker-dialog',
} as const;

const getSprintTopicIds = (sprint: ContentSprint | undefined): string[] => {
  if (!sprint) return [];
  const ids = new Set<string>();
  for (const topicId of (((sprint as any).assignedTopicIds ?? []) as string[])) {
    if (topicId) ids.add(String(topicId));
  }
  for (const assignment of (((sprint as any).assignments ?? []) as Array<{ topicId?: string }>)) {
    if (assignment?.topicId) ids.add(String(assignment.topicId));
  }
  return Array.from(ids);
};

const formatDateValue = (value: unknown): string => {
  if (!value) return '—';
  if (typeof value === 'string') return value.slice(0, 10);
  if (value instanceof Date) return value.toISOString().slice(0, 10);
  if (
    typeof value === 'object'
    && value !== null
    && 'toDate' in value
    && typeof (value as { toDate: () => Date }).toDate === 'function'
  ) {
    return (value as { toDate: () => Date }).toDate().toISOString().slice(0, 10);
  }
  return String(value).slice(0, 10);
};

const formatTitleCase = (value: unknown): string => {
  const raw = String(value ?? '').trim();
  if (!raw) return '—';
  return raw
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
};

const formatPlatformName = (platform: PlatformId | string): string =>
  formatTitleCase(platform === 'twitter' ? 'X / Twitter' : platform);

const getGoalQuotaLabel = (sprint: ContentSprint): string => {
  const quotas = ((sprint.sprintContract?.outputGoalQuotas ?? []) as Array<{
    goal?: string;
    requiredCount?: number;
  }>);
  if (quotas.length === 0) return 'no goal quotas';
  return quotas
    .slice(0, 3)
    .map((quota) => `${quota.goal ?? 'goal'} ${quota.requiredCount ?? 0}`)
    .join(' · ');
};

const countSourceRefs = (sourceData: unknown): number => {
  if (!sourceData || typeof sourceData !== 'object') return 0;
  return Object.values(sourceData as Record<string, unknown>).reduce<number>((sum, value) => {
    if (Array.isArray(value)) return sum + value.length;
    if (value && typeof value === 'object') return sum + countSourceRefs(value);
    return sum;
  }, 0);
};

// ──────────────────────────────────────────────────────────────────────
// Tab configuration (data-driven; reorder by moving array entries).
// Module-level so config identity is stable across renders. Everything is
// keyed by string `id` — never by index — so reordering / adding / removing
// tabs requires only a structural array change. Icons are part of the
// config (data, not render-branch). `as const` makes the structure deeply
// readonly and gives TypeScript exact id literal types.
// ──────────────────────────────────────────────────────────────────────

type TopTabId = 'creator-setup' | 'topics-research' | 'plans';
// Concurrent Minsky refactor — 'post-examples' sub-tab retired.
// Examples are now per-platform in `Creator → Platforms → {platform} → Examples`.
type SubTabId =
  | 'creator' | 'platforms'
  | 'backlog' | 'themes'
  | 'strategies' | 'calendar' | 'engagement';

interface SubTabConfig {
  readonly id: SubTabId;
  readonly label: string;
  readonly icon: SvgIconComponent;
  readonly testId?: string;
}

interface TopTabConfig {
  readonly id: TopTabId;
  readonly label: string;
  readonly subTabs: readonly SubTabConfig[];
}

const TOP_TAB_CONFIG: readonly TopTabConfig[] = [
  {
    id: 'creator-setup',
    label: 'Creator & Setup',
    subTabs: [
      { id: 'creator', label: 'Creator', icon: PersonIcon, testId: 'content-plan-subtab-creator' },
      { id: 'platforms', label: 'Platforms', icon: LanguageIcon, testId: CONTENT_PLAN.SUBTAB_PLATFORMS },
    ],
  },
  {
    id: 'topics-research',
    label: 'Topics & Research',
    subTabs: [
      { id: 'backlog', label: 'Backlog', icon: LightbulbIcon, testId: CONTENT_PLAN.SUBTAB_BACKLOG },
      { id: 'themes', label: 'Themes', icon: PaletteIcon, testId: CONTENT_PLAN.SUBTAB_THEMES },
      // No Scheduled research sub-tab: its UI was removed; the legacy `scheduled`
      // URL value redirects to Backlog (see the rawSubTab normalisation below).
    ],
  },
  {
    id: 'plans',
    label: 'Plans',
    subTabs: [
      { id: 'strategies', label: 'Strategies', icon: RouteIcon, testId: CONTENT_PLAN.SUBTAB_STRATEGIES },
      { id: 'calendar', label: 'Calendar', icon: CalendarMonthIcon, testId: CONTENT_PLAN.SUBTAB_CALENDAR },
      { id: 'engagement', label: 'Engagement', icon: ForumOutlinedIcon, testId: CONTENT_PLAN.SUBTAB_ENGAGEMENT },
    ],
  },
] as const;

/** Reverse lookup: which top-tab a given sub-tab belongs to. Computed once at module load. */
const SUB_TO_TOP: Readonly<Record<SubTabId, TopTabId>> = (() => {
  const map: Partial<Record<SubTabId, TopTabId>> = {};
  for (const top of TOP_TAB_CONFIG) {
    for (const sub of top.subTabs) {
      map[sub.id] = top.id;
    }
  }
  return map as Record<SubTabId, TopTabId>;
})();

const FIRST_TOP_TAB_ID: TopTabId = TOP_TAB_CONFIG[0].id;

const isKnownSubTab = (value: string): value is SubTabId =>
  Object.prototype.hasOwnProperty.call(SUB_TO_TOP, value);

export interface ContentPlansPageProps {
  projectId: string;
  userId: string;
  /** Strategy service — created once in ProjectView; powers CompositeDataPicker auto-loading. */
  strategyService: StrategyService;
  /** Theme catalog for GenerateTopicsDialog (default project themes) */
  themeCatalog?: Array<{ id: string; name: string }>;
  /** Optional: notified when a fire-and-forget BackendTask is started. */
  onTaskStarted?: (taskId: string, label: string) => void;
}

// ─── Internal: URL state helpers ────────────────────────────────────

function useDrillDownState() {
  const location = useLocation();
  const [state, setState] = useState(() => parseDrillDown(window.location.search));
  React.useEffect(() => {
    setState(parseDrillDown(location.search));
  }, [location.search]);
  React.useEffect(() => {
    const onPop = () => setState(parseDrillDown(window.location.search));
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);
  const update = React.useCallback((next: typeof state) => {
    setState(next);
    const currentParams = new URLSearchParams(window.location.search);
    const nextParams = new URLSearchParams(currentParams);
    applyDrillDownState(nextParams, next);
    const qs = nextParams.toString() ? `?${nextParams.toString()}` : '';
    const newUrl = `${window.location.pathname}${qs}${window.location.hash}`;
    window.history.pushState({}, '', newUrl);
  }, []);
  return { state, update };
}

// ─── Backlog sub-tab ───────────────────────────────────────────────

const BacklogTab: React.FC<{
  projectId: string;
  userId: string;
  authorId: string | null;
  strategyService: StrategyService;
  themeCatalog?: Array<{ id: string; name: string }>;
  onOpenTopic: (topic: ContentTopic) => void;
}> = ({ projectId, userId, authorId, strategyService, themeCatalog, onOpenTopic }) => {
  const { topics, loading } = useTopicBacklog(projectId, authorId);
  const { byId: pillarsById } = usePillars(projectId);
  const { runs: topicRuns } = useGenerationRuns(projectId, 'topic', authorId);
  const { markViewed } = useTaskActions();
  const topicGenerationTaskFilter = useMemo(() => ({
    taskType: ['generate-content-plan-topics-batch', 'generate-topics'],
    status: TERMINAL_STATUSES,
    statusMode: 'all' as const,
    ownerMode: 'all' as const,
    unviewedOnly: true,
    ...(authorId ? { authorId } : {}),
  }), [authorId]);
  const unviewedTopicGenerationTasks = useTasks(topicGenerationTaskFilter);

  // Surface pillar-tagged topics first for editor scanning. Stable JS sort
  // preserves the upstream `useTopicBacklog` orderBy(createdAt desc) for ties.
  const sortedTopics = useMemo(() => {
    return [...topics].sort((a: any, b: any) => {
      const ap = Array.isArray(a.pillarIds) && a.pillarIds.length > 0 ? 1 : 0;
      const bp = Array.isArray(b.pillarIds) && b.pillarIds.length > 0 ? 1 : 0;
      return bp - ap; // pillar-bearing first
    });
  }, [topics]);
  const [genOpen, setGenOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  // workflow-golden-tower — manual topic creation (no-AI path validated by
  // /agent/content-plan/topics/manual).
  const [manualTopicOpen, setManualTopicOpen] = useState(false);
  const [reviewRun, setReviewRun] = useState<GenerationRunSummary | null>(null);
  const [applyToast, setApplyToast] = useState<string | null>(null);

  // SDataTable selection state (mirrors PlatformPostsTable pattern).
  const [rowSelection, setRowSelection] = useState<SDataTableRowSelectionState>({});
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [bulkAspectsBusy, setBulkAspectsBusy] = useState(false);
  const [bulkAspectsToast, setBulkAspectsToast] = useState<string | null>(null);

  const selectedIds = useMemo(
    () => Object.keys(rowSelection).filter((id) => rowSelection[id]),
    [rowSelection],
  );

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    if (!confirm(`Delete ${selectedIds.length} topic(s)? This cannot be undone.`)) return;
    setDeleting(true); setDeleteError(null);
    try {
      await topicBacklogFirestoreService.deleteTopics(projectId, selectedIds);
      setRowSelection({});
    } catch (e: any) {
      setDeleteError(e?.message ?? 'Bulk delete failed');
    } finally {
      setDeleting(false);
    }
  };

  /**
   * Bulk aspect generation. Fans out one apiClient.generateAspects call per
   * selected topic, with concurrency cap = 5 (chunked Promise.all). Per
   * product policy, the bulk variant has no `userInstruction` — only
   * author/pillar context is used.
   */
  const handleBulkGenerateAspects = async () => {
    if (selectedIds.length === 0) return;
    if (!confirm(
      `Generate aspects for ${selectedIds.length} topic(s)? Each topic will receive 3 aspects (default) overwriting any existing breakdown.`,
    )) return;
    setBulkAspectsBusy(true);
    setBulkAspectsToast(null);
    try {
      const client = new ContentPlanApiClient();
      const CONCURRENCY = 5;
      const results: Array<{ id: string; ok: boolean; error?: string }> = [];
      for (let i = 0; i < selectedIds.length; i += CONCURRENCY) {
        const chunk = selectedIds.slice(i, i + CONCURRENCY);
        const settled = await Promise.all(
          chunk.map(async (topicId) => {
            try {
              const resp = await client.generateAspects({
                projectId,
                userId,
                authorId: authorId ?? '',
                topicId,
                count: 3,
                // Bulk variant: NO userInstruction (per product policy).
              });
              return { id: topicId, ok: !!resp.success, error: resp.error };
            } catch (e: any) {
              return { id: topicId, ok: false, error: e?.message ?? 'failed' };
            }
          }),
        );
        results.push(...settled);
      }
      const okCount = results.filter((r) => r.ok).length;
      const failCount = results.length - okCount;
      setBulkAspectsToast(
        failCount === 0
          ? `Started aspect generation for ${okCount} topic(s).`
          : `Started ${okCount}/${results.length} — ${failCount} failed to dispatch.`,
      );
      setRowSelection({});
    } finally {
      setBulkAspectsBusy(false);
    }
  };

  const handleDeleteOne = async (topicId: string) => {
    if (!confirm('Delete this topic? This cannot be undone.')) return;
    setDeleteError(null);
    try {
      await topicBacklogFirestoreService.deleteTopics(projectId, [topicId]);
    } catch (e: any) {
      setDeleteError(e?.message ?? 'Delete failed');
    }
  };

  const handleOpenHistory = () => {
    setHistoryOpen(true);
    for (const task of unviewedTopicGenerationTasks) {
      void markViewed(task.id);
    }
  };

  const tableColumns = useMemo<SDataTableColumnDef<ContentTopic>[]>(() => [
    {
      accessorKey: 'status',
      header: 'Status',
      Cell: ({ cell }) => (
        <Chip
          label={(cell.getValue<string>() ?? 'draft')}
          size="small"
          color={cell.getValue<string>() === 'ready' ? 'success' : 'default'}
          variant={cell.getValue<string>() === 'ready' ? 'filled' : 'outlined'}
        />
      ),
      size: 110,
    },
    {
      accessorKey: 'angle',
      header: 'Angle',
      Cell: ({ row }) => (
        <Typography variant="body2" sx={{ whiteSpace: 'normal' }}>
          {(row.original as any).angle ?? '—'}
        </Typography>
      ),
      size: 360,
    },
    {
      // accessorFn (not accessorKey) so the underlying value used by the
      // global search combines `summary` with the `keyMessage` fallback —
      // match-sorter then matches against whichever field actually holds
      // text on a given row. Cell render below preserves the same fallback.
      accessorFn: (row: any) => row.summary ?? row.keyMessage ?? '',
      id: 'summary',
      header: 'Summary',
      Cell: ({ row }) => {
        const summary = (row.original as any).summary ?? (row.original as any).keyMessage;
        return (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ whiteSpace: 'normal' }}
          >
            {summary ? String(summary) : '—'}
          </Typography>
        );
      },
      size: 360,
    },
    {
      accessorKey: 'pillarIds',
      header: 'Pillars',
      Cell: ({ row }) => {
        const ids = (row.original as any).pillarIds as string[] | undefined;
        if (!Array.isArray(ids) || ids.length === 0) return null;
        const primaryId = ((row.original as any).primaryPillarId as string | undefined) ?? ids[0];
        return (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {ids.slice(0, 3).map((pid) => {
              const p = pillarsById[pid];
              const label = p?.name ?? pid.replace(/^pillar_/, '').slice(0, 12);
              const isPrimary = pid === primaryId;
              return (
                <Chip
                  key={pid}
                  label={label}
                  size="small"
                  color={isPrimary ? 'primary' : 'default'}
                  variant={isPrimary ? 'filled' : 'outlined'}
                  data-testid={`backlog-row-pillar-${pid}`}
                />
              );
            })}
          </Box>
        );
      },
      size: 240,
      enableSorting: false,
    },
    {
      accessorKey: 'themeAssignment',
      header: 'Themes',
      Cell: ({ row }) => {
        const arr = ((row.original as any).themeAssignment ?? []) as Array<{ themeId: string }>;
        if (arr.length === 0) return null;
        return (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {arr.slice(0, 3).map((t) => (
              <Chip key={t.themeId} label={t.themeId} size="small" />
            ))}
          </Box>
        );
      },
      size: 200,
      enableSorting: false,
    },
    {
      accessorKey: 'targetPlatforms',
      header: 'Platforms',
      Cell: ({ cell }) => {
        const platforms = (cell.getValue<string[] | undefined>()) ?? [];
        if (platforms.length === 0) return <Typography variant="body2" color="text.secondary">—</Typography>;
        return (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {platforms.slice(0, 4).map((platform) => (
              <Chip key={platform} label={platform} size="small" variant="outlined" />
            ))}
          </Box>
        );
      },
      size: 180,
      enableSorting: false,
    },
    {
      accessorKey: 'targetAudienceIds',
      header: 'Audiences',
      Cell: ({ cell }) => {
        const audiences = (cell.getValue<string[] | undefined>()) ?? [];
        if (audiences.length === 0) return <Typography variant="body2" color="text.secondary">—</Typography>;
        return (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {audiences.slice(0, 3).map((audienceId) => (
              <Chip key={audienceId} label={audienceId} size="small" variant="outlined" />
            ))}
          </Box>
        );
      },
      size: 200,
      enableSorting: false,
    },
    {
      accessorKey: 'weight',
      header: 'Score',
      Cell: ({ cell }) => {
        const v = cell.getValue<number | undefined>();
        return (
          <Typography variant="body2" sx={{ textAlign: 'center' }}>
            {typeof v === 'number' ? v : '—'}
          </Typography>
        );
      },
      size: 80,
    },
    {
      accessorKey: 'usedInSprintIds',
      header: 'Used in',
      Cell: ({ cell }) => {
        const arr = (cell.getValue<string[] | undefined>()) ?? [];
        return (
          <Typography variant="body2" sx={{ textAlign: 'center' }}>
            {arr.length}
          </Typography>
        );
      },
      size: 90,
    },
    {
      accessorKey: 'createdAt',
      header: 'Created',
      Cell: ({ cell }) => (
        <Typography variant="body2" color="text.secondary">
          {formatDateValue(cell.getValue())}
        </Typography>
      ),
      size: 120,
    },
    {
      // Phase 1 — "Open in tab" affordance for the standalone Topic route
      // (`/project/:pid/content-plan/topic/:tid?tab=overview`). Sits in
      // the row's right-side actions; the existing kebab menu (delete,
      // open dialog) stays unchanged.
      id: 'openInTab',
      header: '',
      enableSorting: false,
      enableColumnActions: false,
      Cell: ({ row }) => {
        const topicId = (row.original as any).id as string | undefined;
        if (!topicId) return null;
        return (
          <Tooltip title="Open in new tab">
            <IconButton
              size="small"
              data-testid={`topic-row-open-in-tab-${topicId}`}
              onClick={(e) => {
                e.stopPropagation();
                window.open(
                  getTopicRoutePath({ projectId, topicId, tab: 'overview', authorId }),
                  '_blank',
                );
              }}
            >
              <OpenInNewIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        );
      },
      size: 60,
    },
  ], [pillarsById, projectId]);

  const readyCount = topics.filter((t) => t.status === 'ready').length;
  const draftCount = topics.filter((t) => t.status === 'draft').length;

  return (
    <Box
      data-testid={CONTENT_PLAN.BACKLOG_TABLE}
      sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 2, flexShrink: 0 }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <Box>
            <Typography variant="h6">Topic Backlog</Typography>
            <Typography variant="caption" color="text.secondary">
              {topics.length} topics · {readyCount} ready · {draftCount} draft
            </Typography>
          </Box>
          {selectedIds.length > 0 && (
            <Stack direction="row" alignItems="center" spacing={1} sx={{ ml: 2 }}>
              <Chip
                label={selectedIds.length}
                size="small"
                color="primary"
                sx={{ fontWeight: 'medium' }}
                data-testid="backlog-selected-count"
              />
              <Tooltip title="Delete selected">
                <span>
                  <IconButton
                    onClick={handleBulkDelete}
                    color="error"
                    size="small"
                    disabled={deleting}
                    data-testid="backlog-bulk-delete"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title={`Generate 3 aspects for each of ${selectedIds.length} selected topic(s)`}>
                <span>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<AutoFixHighIcon />}
                    onClick={handleBulkGenerateAspects}
                    disabled={bulkAspectsBusy}
                    data-testid="bulk-aspects-generate-btn"
                    sx={{ textTransform: 'none' }}
                  >
                    {bulkAspectsBusy
                      ? 'Generating…'
                      : `Generate aspects (${selectedIds.length})`}
                  </Button>
                </span>
              </Tooltip>
              <Button
                size="small"
                onClick={() => setRowSelection({})}
                sx={{ textTransform: 'none' }}
              >
                Clear selection
              </Button>
            </Stack>
          )}
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center">
          <Button
            variant="outlined"
            startIcon={
              <Badge
                color="error"
                badgeContent={unviewedTopicGenerationTasks.length}
                invisible={unviewedTopicGenerationTasks.length === 0}
                overlap="circular"
              >
                <HistoryIcon fontSize="small" />
              </Badge>
            }
            onClick={handleOpenHistory}
            data-testid={CONTENT_PLAN.TOPIC_GENERATION_HISTORY_BUTTON}
          >
            Generation history
          </Button>
          <Button
            variant="contained"
            startIcon={<AutoFixHighIcon />}
            onClick={() => setGenOpen(true)}
            data-testid={CONTENT_PLAN.OPEN_GEN_TOPICS}
          >
            Generate topics
          </Button>
          {/* workflow-golden-tower — directive (no-AI) topic creation. */}
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => setManualTopicOpen(true)}
            data-testid="backlog-add-manual-topic"
          >
            + Add manually
          </Button>
        </Stack>
      </Stack>

      {deleteError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setDeleteError(null)}>
          {deleteError}
        </Alert>
      )}
      {bulkAspectsToast && (
        <Alert severity="info" sx={{ mb: 2 }} onClose={() => setBulkAspectsToast(null)}>
          {bulkAspectsToast}
        </Alert>
      )}

      {loading ? (
        <Typography variant="body2">Loading backlog…</Typography>
      ) : topics.length === 0 ? (
        <Alert severity="info">
          Backlog is empty. Click "Generate topics" to populate it via AI.
        </Alert>
      ) : (
        <Box sx={{ flex: 1, minHeight: 0 }}>
          <SDataTable<ContentTopic>
            columns={tableColumns}
            data={sortedTopics as ContentTopic[]}
            getRowId={(row) => (row as any).id ?? ''}
            enableRowSelection
            enableRowActions
            enableSorting
            enableTopToolbar={false}
            enableSearch
            searchInColumn="angle"
            searchMode="global"
            searchPlaceholder="Search angle, summary, key message…"
            enableBottomToolbar
            enablePagination
            selectAllMode="all"
            initialState={{ pagination: { pageSize: 50, pageIndex: 0 } }}
            muiPaginationProps={{
              rowsPerPageOptions: [25, 50, 100],
              showFirstButton: true,
              showLastButton: true,
            }}
            enableStickyHeader
            state={{ rowSelection }}
            onRowSelectionChange={setRowSelection}
            muiTablePaperProps={{
              variant: 'outlined',
              elevation: 0,
              sx: {
                height: '100%',
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
              },
            }}
            muiTableContainerProps={{
              sx: {
                flex: 1,
                minHeight: 0,
                height: '100%',
                maxHeight: 'none',
                overflow: 'auto',
              },
            }}
            muiTableHeadCellProps={{
              sx: {
                bgcolor: 'action.hover',
                borderBottom: '2px solid',
                borderColor: 'divider',
                fontWeight: 'fontWeightMedium',
                position: 'sticky',
                top: 0,
                zIndex: 3,
              },
            }}
            muiTableProps={{
              size: 'small',
              sx: {
                minWidth: 1760,
                tableLayout: 'fixed',
                overflow: 'visible',
              },
            }}
            muiSelectCheckboxProps={{ size: 'small' }}
            muiSelectAllCheckboxProps={{ size: 'small' }}
            muiTableBodyRowProps={({ row }) => ({
              hover: true,
              'data-testid': `topic-backlog-row-${(row.original as ContentTopic).id}`,
              onClick: (e) => {
                // Skip when click originated inside the checkbox or actions cell
                // (MRT v3 propagates raw clicks; we filter on the cell role).
                const target = e.target as HTMLElement;
                if (target.closest('.MuiCheckbox-root')) return;
                if (target.closest('button')) return;
                onOpenTopic(row.original as ContentTopic);
              },
              sx: { cursor: 'pointer' },
            })}
            renderRowActionMenuItems={({ row, closeMenu }) => [
              <MenuItem
                key="open"
                onClick={() => {
                  closeMenu();
                  onOpenTopic(row.original as ContentTopic);
                }}
              >
                <OpenInNewIcon fontSize="small" sx={{ mr: 1 }} />
                Open detail
              </MenuItem>,
              <MenuItem
                key="delete"
                onClick={() => {
                  closeMenu();
                  const id = (row.original as any).id;
                  if (id) handleDeleteOne(id);
                }}
                sx={{ color: 'error.main' }}
              >
                <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
                Delete
              </MenuItem>,
            ]}
          />
        </Box>
      )}

      <SDialog
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        title="Generation history"
        maxWidth="lg"
        fullWidth
        data-testid={CONTENT_PLAN.TOPIC_GENERATION_HISTORY_DIALOG}
      >
        <Box sx={{ px: 3, pb: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Past AI topic-generation runs. Accepted topics get promoted into the backlog with
            <Box component="code" sx={{ mx: 0.5 }}>status: 'ready'</Box>.
          </Typography>
          <GenerationHistoryList
            runs={topicRuns}
            onRowClick={(run) => setReviewRun(run)}
            emptyHint="No AI generation runs yet. Click 'Generate topics' above to create one."
          />
        </Box>
      </SDialog>

      <GenerateTopicsDialog
        open={genOpen}
        onClose={() => setGenOpen(false)}
        projectId={projectId}
        userId={userId}
        authorId={authorId ?? ''}
        strategyService={strategyService}
        themeCatalog={themeCatalog ?? []}
      />

      {/* workflow-golden-tower — manual topic creation (validated, no-AI). */}
      <ManualTopicDialog
        open={manualTopicOpen}
        onClose={() => setManualTopicOpen(false)}
        projectId={projectId}
        userId={userId}
        authorId={authorId ?? ''}
      />

      {/* Topic review dialog — shared component from @modules/content-plan/generations */}
      <GenerationReviewDialog
        open={Boolean(reviewRun)}
        onClose={() => setReviewRun(null)}
        projectId={projectId}
        entity="topic"
        run={reviewRun}
        titleSuffix={reviewRun ? `${reviewRun.generatedItemCount ?? 0} drafts` : undefined}
        onApply={async (acceptedItemIds) => {
          if (!reviewRun) return;
          const client = new ContentPlanApiClient();
          const res = await client.applyTopics({ projectId, authorId: authorId ?? '', generationId: reviewRun.id, acceptedItemIds });
          if (!res.success) throw new Error((res as any).error ?? 'apply failed');
          setApplyToast(`Applied ${res.appliedCount} topics · skipped ${res.skippedCount} · rejected ${res.rejectedCount}`);
        }}
        renderItem={(draft) => {
          const data = draft.data as unknown as { angle?: string; summary?: string; themeAssignment?: Array<{ themeId: string }>; weight?: number };
          const dedupFlag = Boolean((draft as any).rejectedByDedup);
          return (
            <Stack spacing={0.5}>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                <Typography variant="subtitle2">{data.angle ?? draft.id}</Typography>
                {dedupFlag && <Chip size="small" color="warning" label="near-duplicate" />}
                {typeof data.weight === 'number' && <Chip size="small" label={`w ${data.weight}`} variant="outlined" />}
              </Stack>
              {data.summary && (
                <Typography variant="caption" color="text.secondary">{data.summary}</Typography>
              )}
              {(data.themeAssignment ?? []).length > 0 && (
                <Stack direction="row" flexWrap="wrap" gap={0.3}>
                  {(data.themeAssignment ?? []).slice(0, 4).map((t, i) => (
                    <Chip key={`${draft.id}_t${i}`} size="small" variant="outlined" label={t.themeId} sx={{ fontSize: '0.65em' }} />
                  ))}
                </Stack>
              )}
            </Stack>
          );
        }}
      />

      {applyToast && (
        <Alert severity="success" onClose={() => setApplyToast(null)} sx={{ position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)', zIndex: 9999 }}>
          {applyToast}
        </Alert>
      )}
    </Box>
  );
};

// ─── Strategies list ───────────────────────────────────────────────

const StrategiesTable: React.FC<{
  projectId: string;
  authorId: string | null;
  readOnly?: boolean;
  onOpenStrategy: (id: string) => void;
  onCreateClick: () => void;
  onDeleted?: (id: string) => void;
}> = ({ projectId, authorId, readOnly = false, onOpenStrategy, onCreateClick, onDeleted }) => {
  const [strategies, setStrategies] = useState<ContentStrategy[]>([]);
  const [deletingStrategyId, setDeletingStrategyId] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [deleteNotice, setDeleteNotice] = useState<string | null>(null);

  React.useEffect(() => {
    const svc = new ContentStrategyFirestoreService();
    const unsub = svc.subscribeStrategies(projectId, authorId, setStrategies);
    return unsub;
  }, [projectId, authorId]);

  const handleDeleteStrategy = async (strategy: ContentStrategy) => {
    const strategyId = strategy.id;
    if (!strategyId || deletingStrategyId) return;
    const confirmed = window.confirm(
      `Delete strategy "${strategy.name ?? strategyId}" and all generated sprints, publications, posts, and post-generation drafts? This cannot be undone.`,
    );
    if (!confirmed) return;

    setDeletingStrategyId(strategyId);
    setDeleteError(null);
    setDeleteNotice(null);
    try {
      const svc = new ContentStrategyFirestoreService();
      const result = await svc.deleteStrategyWithArtifacts(projectId, strategyId);
      onDeleted?.(strategyId);
      setDeleteNotice(
        `Deleted strategy and artifacts: ${result.sprints} sprint(s), ${result.publications} publication(s), ${result.contentPosts} post(s).`,
      );
    } catch (e) {
      setDeleteError((e as Error).message || 'Failed to delete strategy.');
    } finally {
      setDeletingStrategyId(null);
    }
  };

  const columns: TableColumn<any>[] = [
    { key: 'name', label: 'Name', width: '30%' },
    {
      key: 'dateStart',
      label: 'Date range',
      format: (_: any, row: any) => `${row.dateStart ?? '?'} → ${row.dateEnd ?? '?'}`,
    },
    {
      key: 'targetPlatforms',
      label: 'Platforms',
      format: (arr: any) => (arr ?? []).join(', '),
    },
    {
      key: 'status',
      label: 'Status',
      format: (v: any) => <Chip label={v ?? 'draft'} size="small" data-testid={CONTENT_PLAN.STATUS_BADGE} />,
    },
    {
      key: 'actions',
      label: 'Actions',
      width: '12%',
      format: (_: any, row: ContentStrategy) => {
        const strategyId = row.id;
        const deleting = deletingStrategyId === strategyId;
        return (
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Tooltip title="View strategy">
              <IconButton
                size="small"
                aria-label={`View strategy ${row.name ?? strategyId}`}
                onClick={(event) => {
                  event.stopPropagation();
                  if (strategyId) onOpenStrategy(strategyId);
                }}
              >
                <OpenInNewIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete strategy">
              <span>
                <IconButton
                  size="small"
                  color="error"
                  disabled={readOnly || Boolean(deletingStrategyId)}
                  aria-label={`Delete strategy ${row.name ?? strategyId}`}
                  onClick={(event) => {
                    event.stopPropagation();
                    void handleDeleteStrategy(row);
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            {deleting && (
              <Typography variant="caption" color="text.secondary">
                Deleting…
              </Typography>
            )}
          </Stack>
        );
      },
    },
  ];

  return (
    <Box data-testid={CONTENT_PLAN.STRATEGIES_TABLE}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography variant="h6">Content Strategies</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={onCreateClick}
          disabled={readOnly}
          data-testid={CONTENT_PLAN.OPEN_DIALOG_BTN}
        >
          New Strategy
        </Button>
      </Stack>
      {strategies.length === 0 ? (
        <Alert severity="info">
          {readOnly
            ? 'No content strategies are visible in this overview yet.'
            : 'No content strategies yet. Click "New Strategy" to generate one.'}
        </Alert>
      ) : (
        <>
          {deleteError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setDeleteError(null)}>
              {deleteError}
            </Alert>
          )}
          {deleteNotice && (
            <Alert severity="success" sx={{ mb: 2 }} onClose={() => setDeleteNotice(null)}>
              {deleteNotice}
            </Alert>
          )}
          <DynamicTable
            data={strategies as any[]}
            columns={columns}
            keyField={'id' as any}
            onRowClick={(row) => onOpenStrategy((row as any).id)}
          />
        </>
      )}
    </Box>
  );
};

// ─── Strategy detail dialog ────────────────────────────────────────

const StrategyDetailsDialog: React.FC<{
  open: boolean;
  projectId: string;
  userId: string;
  authorId: string | null;
  strategyId: string | null | undefined;
  onClose: () => void;
  onOpenSprint: (id: string) => void;
  /** LT1 — persistent progress surface; replaces the one-line task toast. */
  onTaskLaunched?: (taskId: string, label: string) => void;
}> = ({ open, projectId, userId, authorId, strategyId, onClose, onOpenSprint, onTaskLaunched }) => {
  const navigate = useNavigate();
  const { strategy, sprints, loading } = useContentStrategy(projectId, strategyId ?? null);
  const projectDefaultModel = useProjectDefaultModel(projectId);
  const [generationModel, setGenerationModel] = useState(projectDefaultModel.value);
  const [generating, setGenerating] = useState(false);
  const [genError, setGenError] = useState<string | null>(null);
  const [genNotice, setGenNotice] = useState<string | null>(null);

  React.useEffect(() => {
    if (!open) return;
    setGenerationModel(projectDefaultModel.value);
    setGenError(null);
    setGenNotice(null);
  }, [open, projectDefaultModel.value, strategyId]);

  const assignedTopicCount = useMemo(
    () => sprints.reduce((sum, sprint) => sum + getSprintTopicIds(sprint).length, 0),
    [sprints],
  );

  const strategyWithCalendar = strategy as CalendarAwareContentStrategy | null;
  const platformCadences = (strategyWithCalendar?.platformCadences ?? []) as StrategyPlatformCadence[];
  const postsPerWeekPerPlatform = ((strategy as any)?.postsPerWeekPerPlatform ?? {}) as Record<string, number>;
  const platforms = useMemo(() => {
    const ids = new Set<string>();
    for (const platform of ((strategy?.targetPlatforms ?? []) as PlatformId[])) ids.add(String(platform));
    for (const cadence of platformCadences) ids.add(String(cadence.platform));
    for (const platform of Object.keys(postsPerWeekPerPlatform)) ids.add(platform);
    return Array.from(ids) as PlatformId[];
  }, [platformCadences, postsPerWeekPerPlatform, strategy?.targetPlatforms]);

  const handleGenerateAll = async () => {
    if (!strategy || !strategyId) return;
    if (assignedTopicCount === 0) {
      setGenNotice(null);
      setGenError('No sprint topic assignments yet. Regenerate the strategy with auto topic assignment, or assign topics before generating posts.');
      return;
    }
    setGenerating(true); setGenError(null); setGenNotice(null);
    try {
      const client = new ContentPlanApiClient();
      const r = await client.generatePosts({
        projectId, userId, authorId: authorId ?? '',
        strategyId,
        scope: 'strategy',
        models: { platformPost: generationModel },
      });
      if (!r.success) throw new Error(r.error ?? 'generate failed');
      // LT1: the persistent wizard task banner is the PRIMARY progress
      // surface; this inline notice is only a pointer to it.
      onTaskLaunched?.(r.taskId, 'Generate posts (strategy)');
      setGenNotice('Post generation started — live progress is in the flow banner above the tabs.');
    } catch (e) {
      setGenError((e as Error).message);
    } finally {
      setGenerating(false);
    }
  };

  // DE3 — inline assignment action for the "no sprint assignments" state.
  const [assigningTopics, setAssigningTopics] = useState(false);
  const [assignError, setAssignError] = useState<string | null>(null);
  const handleAssignTopics = async () => {
    if (!strategyId || sprints.length === 0) return;
    setAssigningTopics(true); setAssignError(null);
    try {
      const client = new ContentPlanApiClient();
      const target = sprints.find((sprint) => getSprintTopicIds(sprint).length === 0) ?? sprints[0];
      const r = await client.generateTopicsForSprint(strategyId, target.id, {
        projectId, userId, authorId: authorId ?? '',
      });
      if (!r.success) throw new Error(r.error ?? 'Topic assignment failed to start');
      onTaskLaunched?.(r.taskId, 'Generate & assign sprint topics');
      setGenNotice('Sprint topic generation started — review the drafts from the sprint detail when it completes.');
    } catch (e) {
      // CTA stays actionable — error shows next to it (AC-A042).
      setAssignError((e as Error).message);
    } finally {
      setAssigningTopics(false);
    }
  };

  const sprintColumns: TableColumn<any>[] = [
    { key: 'sprintType', label: 'Type', width: '12%', format: (v: any) => <Chip label={v ?? '?'} size="small" /> },
    { key: 'dateStart', label: 'Start', width: '12%', format: (v: any) => formatDateValue(v) },
    { key: 'dateEnd', label: 'End', width: '12%', format: (v: any) => formatDateValue(v) },
    { key: 'goal', label: 'Goal', width: '40%', format: (v: any) => v ?? '—' },
    {
      key: 'assignedTopicIds',
      label: 'Topics',
      width: '8%',
      align: 'center',
      format: (_: any, row: ContentSprint) => getSprintTopicIds(row).length,
    },
    {
      key: 'sprintContract',
      label: 'Goal mix',
      width: '16%',
      format: (_: any, row: ContentSprint) => (
        <Typography variant="caption" color="text.secondary">
          {getGoalQuotaLabel(row)}
        </Typography>
      ),
    },
  ];

  const renderCadenceSlots = (cadence: StrategyPlatformCadence) => {
    const slots: Array<{ id: string; day: string; time: string; score: string }> = cadence.slots && cadence.slots.length > 0
      ? cadence.slots.map((slot, index) => ({
          id: `${cadence.platform}-${index}`,
          day: CADENCE_DAY_CODES[slot.weekdayIndex] ?? '—',
          time: slot.timeLocal,
          score: typeof slot.score === 'number' ? slot.score.toFixed(2) : '—',
        }))
      : [];

    if (slots.length === 0 && cadence.preferredTimesLocal?.length) {
      const weekdays = cadence.preferredWeekdays ?? [];
      const times = cadence.preferredTimesLocal ?? [];
      const count = Math.max(weekdays.length, times.length);
      for (let index = 0; index < count; index += 1) {
        const weekday = weekdays.length > 0 ? weekdays[index % weekdays.length] : undefined;
        slots.push({
          id: `${cadence.platform}-legacy-${index}`,
          day: weekday === undefined ? 'Any' : (CADENCE_DAY_CODES[weekday] ?? '—'),
          time: times[index % times.length] ?? '—',
          score: '—',
        });
      }
    }

    if (slots.length === 0) {
      return (
        <Typography variant="caption" color="text.secondary">
          No exact posting slots saved for this platform.
        </Typography>
      );
    }

    return (
      <Stack spacing={0.75}>
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: '96px 96px 1fr',
          gap: 1,
        }}
        >
          <Typography variant="caption" color="text.secondary">Day</Typography>
          <Typography variant="caption" color="text.secondary">Time</Typography>
          <Typography variant="caption" color="text.secondary">Score</Typography>
        </Box>
        {slots.map((slot) => (
          <Box
            key={slot.id}
            sx={{
              display: 'grid',
              gridTemplateColumns: '96px 96px 1fr',
              gap: 1,
              alignItems: 'center',
            }}
          >
            <Typography variant="body2">{slot.day}</Typography>
            <Typography variant="body2">{slot.time}</Typography>
            <Typography variant="body2" color="text.secondary">{slot.score}</Typography>
          </Box>
        ))}
      </Stack>
    );
  };

  return (
    <BaseDialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth={false}
      data-testid={CONTENT_PLAN.STRATEGY_DETAIL_DRAWER}
      sx={{
        width: { xs: 'calc(100vw - 24px)', md: 1120 },
        maxWidth: 'calc(100vw - 24px)',
        height: { xs: 'calc(100vh - 24px)', md: 'min(880px, calc(100vh - 48px))' },
        maxHeight: 'calc(100vh - 24px)',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <DialogHeader title="Strategy details" onClose={onClose} />
      <DialogContent maxHeight="none" sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
        {loading ? (
          <Alert severity="info">Loading strategy…</Alert>
        ) : !strategy ? (
          <Alert severity="warning">Strategy not found.</Alert>
        ) : (
          <Stack spacing={3} sx={{ py: 1 }}>
            {/* workflow-golden-tower — banner when the strategy is in the
                B1 draft state. The Approve button + audience editing lives
                on the dedicated review screen, NOT in this legacy dialog. */}
            {(strategy as any).approvalStage === 'draft_pending_approval' && strategyId && (
              <Alert
                severity="info"
                action={
                  <Button
                    color="inherit"
                    size="small"
                    onClick={() => {
                      onClose();
                      const params = new URLSearchParams();
                      if (authorId) params.set('author', authorId);
                      const suffix = params.toString() ? `?${params.toString()}` : '';
                      navigate(`/project/${projectId}/content-plan/strategy/${strategyId}/review${suffix}`);
                    }}
                    data-testid="strategy-details-open-review"
                  >
                    Open review &amp; approve
                  </Button>
                }
                data-testid="strategy-details-draft-banner"
              >
                This strategy is a B1 draft. To assign topics and emit publications, open the
                review screen and click <strong>Approve &amp; generate</strong>.
              </Alert>
            )}
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>{strategy.name}</Typography>
              <Typography variant="body2" color="text.secondary">
                {formatDateValue(strategy.dateStart)} → {formatDateValue(strategy.dateEnd)}
                {' · '}
                {((strategy.targetPlatforms ?? []) as PlatformId[]).map(formatPlatformName).join(', ') || 'No platforms'}
              </Typography>
            </Box>

            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Chip size="small" label={strategy.status ?? 'draft'} />
              <Chip size="small" variant="outlined" label={`Goal: ${formatTitleCase(strategy.primaryGoal)}`} />
              <Chip size="small" variant="outlined" label={`Mix: ${formatTitleCase(strategy.contentMixPreset)}`} />
              <Chip size="small" variant="outlined" label={`Weeks: ${strategy.weekCount ?? (sprints.length || '—')}`} />
              {strategy.timezone && <Chip size="small" variant="outlined" label={strategy.timezone} />}
            </Stack>

            <Divider />

            <Box>
              <Typography variant="subtitle2" gutterBottom>Strategy setup</Typography>
              <Box sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                gap: 2,
              }}
              >
                <TextField label="Strategy name" value={strategy.name ?? ''} fullWidth InputProps={{ readOnly: true }} />
                <TextField
                  label="Date range"
                  value={`${formatDateValue(strategy.dateStart)} → ${formatDateValue(strategy.dateEnd)}`}
                  fullWidth
                  InputProps={{ readOnly: true }}
                />
                <TextField label="Primary goal" value={formatTitleCase(strategy.primaryGoal)} fullWidth InputProps={{ readOnly: true }} />
                <TextField label="Content mix" value={formatTitleCase(strategy.contentMixPreset)} fullWidth InputProps={{ readOnly: true }} />
                <TextField
                  label="Source refs"
                  value={countSourceRefs(strategy.sourceData)}
                  fullWidth
                  InputProps={{ readOnly: true }}
                />
                <TextField
                  label="Creator research"
                  value={strategy.creatorResearchEnabled ? 'Enabled' : 'Disabled'}
                  fullWidth
                  InputProps={{ readOnly: true }}
                />
              </Box>
            </Box>

            <Divider />

            <Box>
              <Typography variant="subtitle2" gutterBottom>Target platforms and cadence</Typography>
              {platforms.length === 0 ? (
                <Alert severity="info">No platform configuration saved on this strategy.</Alert>
              ) : (
                <Stack spacing={1.5}>
                  {platforms.map((platform) => {
                    const cadence = platformCadences.find((item) => item.platform === platform);
                    const weeklyCount = cadence
                      ? postsPerWeekFromCadence(cadence)
                      : postsPerWeekPerPlatform[String(platform)] ?? 0;
                    return (
                      <Paper key={platform} variant="outlined" sx={{ p: 1.5 }}>
                        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.25 }}>
                          <Typography sx={{ fontWeight: 600, flex: 1 }}>{formatPlatformName(platform)}</Typography>
                          <Chip size="small" variant="outlined" label={`${weeklyCount} / week`} />
                        </Stack>
                        {cadence ? renderCadenceSlots(cadence) : (
                          <Typography variant="caption" color="text.secondary">
                            Weekly quota saved without exact posting slots.
                          </Typography>
                        )}
                      </Paper>
                    );
                  })}
                </Stack>
              )}
            </Box>

            <Divider />

            <Box data-testid={CONTENT_PLAN.SPRINTS_TABLE}>
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={1.5}
                justifyContent="space-between"
                alignItems={{ xs: 'stretch', md: 'center' }}
                sx={{ mb: 2 }}
              >
                <Box>
                  <Typography variant="subtitle2">Sprints</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {sprints.length} sprint(s) · {assignedTopicCount} assigned topic(s)
                  </Typography>
                </Box>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }}>
                  <AvailableModelSelector
                    projectId={projectId}
                    value={generationModel}
                    onChange={setGenerationModel}
                    label="AI model"
                    size="small"
                    sx={{ minWidth: 240 }}
                  />
                  <Button
                    variant="contained"
                    startIcon={<AutoFixHighIcon />}
                    onClick={handleGenerateAll}
                    disabled={generating || sprints.length === 0 || assignedTopicCount === 0}
                    data-testid={CONTENT_PLAN.GENERATE_STRATEGY_POSTS}
                  >
                    {generating ? 'Starting…' : 'Generate ALL posts'}
                  </Button>
                </Stack>
              </Stack>
              {genError && <Alert severity="error" sx={{ mb: 2 }}>{genError}</Alert>}
              {genNotice && <Alert severity="info" sx={{ mb: 2 }}>{genNotice}</Alert>}
              {sprints.length > 0 && assignedTopicCount === 0 && (
                <Alert
                  severity="info"
                  sx={{ mb: 2 }}
                  action={
                    <Button
                      color="inherit"
                      size="small"
                      onClick={handleAssignTopics}
                      disabled={assigningTopics}
                      data-testid={CONTENT_PLAN.STRATEGY_ASSIGN_TOPICS_CTA}
                    >
                      {assigningTopics ? 'Starting…' : 'Assign topics'}
                    </Button>
                  }
                >
                  No sprint topic assignments yet. "Assign topics" generates topics for the
                  first empty sprint; you can also regenerate the strategy with auto topic
                  assignment.
                </Alert>
              )}
              {assignError && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setAssignError(null)}>
                  {assignError}
                </Alert>
              )}
              {sprints.length === 0 ? (
                <Alert severity="info">No sprints yet.</Alert>
              ) : (
                <DynamicTable
                  data={sprints as any[]}
                  columns={sprintColumns}
                  keyField={'id' as any}
                  onRowClick={(row) => onOpenSprint((row as any).id)}
                />
              )}
            </Box>
          </Stack>
        )}
      </DialogContent>
      <DialogFooter
        primaryAction={{
          label: 'Close',
          onClick: onClose,
        } as any}
      />
    </BaseDialog>
  );
};

// ─── Sprint detail (topics list + Generate posts for sprint) ───────

const SprintDetail: React.FC<{
  projectId: string;
  userId: string;
  authorId: string | null;
  strategyId: string;
  sprintId: string;
  onOpenTopic: (topic: ContentTopic) => void;
  /** LT1 — persistent progress surface; replaces the one-line task toast. */
  onTaskLaunched?: (taskId: string, label: string) => void;
}> = ({ projectId, userId, authorId, strategyId, sprintId, onOpenTopic, onTaskLaunched }) => {
  const { sprints } = useContentStrategy(projectId, strategyId);
  const { topics } = useTopicBacklog(projectId, authorId);
  const { runs: postRuns } = useGenerationRuns(projectId, 'post', authorId);
  const projectDefaultModel = useProjectDefaultModel(projectId);
  // Sprint canvas — applied posts live at
  // content_strategies/{sid}/sprints/{sprintId}/posts/*. Click-to-open same dialog.
  const { posts: sprintPosts, loading: sprintPostsLoading } = useSprintPosts(
    projectId, strategyId, sprintId, authorId,
  );
  const { updatePost } = useUpdateSprintPost(projectId, strategyId, sprintId);
  // Concurrent Minsky refactor — promote-to-example path is no-op pending
  // UnifiedPostDialog rewire. Hook destructure removed.
  // Concurrent Minsky refactor — sprint posts are now `Post` (canonical type).
  const [editingSprintPost, setEditingSprintPost] = useState<Post | null>(null);
  const [editingDraft, setEditingDraft] = useState<ContentPost | null>(null);
  const [generationModel, setGenerationModel] = useState(projectDefaultModel.value);
  const sprint = sprints.find((s) => s.id === sprintId);

  React.useEffect(() => {
    setGenerationModel(projectDefaultModel.value);
  }, [projectDefaultModel.value, sprintId]);
  const [generating, setGenerating] = useState(false);
  const [genNotice, setGenNotice] = useState<string | null>(null);
  const [postReviewRun, setPostReviewRun] = useState<GenerationRunSummary | null>(null);
  const [postApplyToast, setPostApplyToast] = useState<string | null>(null);

  // Only surface post-generation runs for THIS sprint.
  const sprintRuns = React.useMemo(() => postRuns.filter((r) => {
    const scope = (r as any).scope;
    const runSprint = (r as any).sprintId;
    const runTopics = ((r as any).topicIds ?? []) as string[];
    if (scope === 'sprint') return runSprint === sprintId;
    if (scope === 'topic') {
      const assigned = new Set(getSprintTopicIds(sprint));
      return runTopics.some((tid) => assigned.has(tid));
    }
    // scope === 'strategy' — include if strategy matches
    return (r as any).strategyId === strategyId;
  }), [postRuns, sprintId, sprint, strategyId]);

  const assignedTopics = useMemo(() => {
    if (!sprint) return [] as ContentTopic[];
    const ids = getSprintTopicIds(sprint);
    return topics.filter((t) => ids.includes(t.id));
  }, [sprint, topics]);

  const handleGenerateSprint = async () => {
    if (assignedTopics.length === 0) {
      setGenNotice('No topics assigned to this sprint yet. Assign topics before generating posts.');
      return;
    }
    setGenerating(true); setGenNotice(null);
    try {
      const client = new ContentPlanApiClient();
      const r = await client.generatePosts({
        projectId,
        userId,
        authorId: authorId ?? '',
        strategyId,
        scope: 'sprint',
        sprintId,
        models: { platformPost: generationModel },
      });
      if (r.success) {
        // LT1: persistent wizard banner is the primary surface for this task.
        onTaskLaunched?.(r.taskId, 'Generate posts (sprint)');
        setGenNotice('Post generation started — live progress is in the flow banner above the tabs.');
      } else {
        setGenNotice(r.error ?? 'failed');
      }
    } catch (e) {
      setGenNotice((e as Error).message);
    } finally {
      setGenerating(false);
    }
  };

  // DE4 — inline generate-topics action for a 0-topic sprint.
  const [sprintTopicsBusy, setSprintTopicsBusy] = useState(false);
  const [sprintTopicsError, setSprintTopicsError] = useState<string | null>(null);
  const handleGenerateSprintTopics = async () => {
    setSprintTopicsBusy(true); setSprintTopicsError(null);
    try {
      const client = new ContentPlanApiClient();
      const r = await client.generateTopicsForSprint(strategyId, sprintId, {
        projectId, userId, authorId: authorId ?? '',
      });
      if (!r.success) throw new Error(r.error ?? 'Topic generation failed to start');
      onTaskLaunched?.(r.taskId, 'Generate sprint topics');
      setGenNotice('Sprint topic generation started — drafts will appear in the generation history below.');
    } catch (e) {
      // CTA stays actionable — error shows next to it (AC-A042).
      setSprintTopicsError((e as Error).message);
    } finally {
      setSprintTopicsBusy(false);
    }
  };

  if (!sprint) return <Alert severity="warning">Sprint not found.</Alert>;

  const columns: TableColumn<any>[] = [
    { key: 'angle', label: 'Angle', width: '50%' },
    {
      key: 'themeAssignment', label: 'Themes',
      format: (arr: any) => (arr ?? [])
        .slice(0, 2)
        .map((t: any) => <Chip key={t.themeId} label={t.themeId} size="small" sx={{ mr: 0.5 }} />),
    },
    { key: 'weight', label: 'Score' },
  ];

  return (
    <Box data-testid={CONTENT_PLAN.TOPICS_TABLE}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h6">Week: {(sprint as any).goal ?? sprintId}</Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {(sprint as any).dateStart} → {(sprint as any).dateEnd} · {(sprint as any).sprintType}
          </Typography>
        </Box>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }}>
          <AvailableModelSelector
            projectId={projectId}
            value={generationModel}
            onChange={setGenerationModel}
            label="AI model"
            size="small"
            sx={{ minWidth: 240 }}
          />
          <Button
            variant="contained"
            startIcon={<AutoFixHighIcon />}
            onClick={handleGenerateSprint}
            disabled={generating || assignedTopics.length === 0}
            data-testid={CONTENT_PLAN.GENERATE_SPRINT_POSTS}
          >
            {generating ? 'Starting…' : `Generate posts (${assignedTopics.length} topics)`}
          </Button>
        </Stack>
      </Stack>
      {genNotice && <Alert severity="info" sx={{ mb: 2 }}>{genNotice}</Alert>}

      {sprintTopicsError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSprintTopicsError(null)}>
          {sprintTopicsError}
        </Alert>
      )}

      {assignedTopics.length === 0 ? (
        <Alert
          severity="info"
          action={
            <Button
              color="inherit"
              size="small"
              onClick={handleGenerateSprintTopics}
              disabled={sprintTopicsBusy}
              data-testid={CONTENT_PLAN.SPRINT_GENERATE_TOPICS_CTA}
            >
              {sprintTopicsBusy ? 'Starting…' : 'Generate topics'}
            </Button>
          }
        >
          No topics assigned to this sprint yet. "Generate topics" creates
          sprint-scoped topic drafts you can review and apply below.
        </Alert>
      ) : (
        <DynamicTable
          data={assignedTopics as any[]}
          columns={columns}
          keyField={'id' as any}
          onRowClick={(row) => onOpenTopic(row as ContentTopic)}
        />
      )}

      {/* Applied posts — canonical sprint posts after "Apply to sprint". Uses
          UnifiedPostCell so the presentation matches Post Examples and draft
          review surfaces. Click a row → UnifiedPostDialog for inline edit +
          asset generation via ownerKind='sprint_post'. */}
      <Box sx={{ mt: 4 }} data-testid="sprint-applied-posts">
        <Typography variant="h6" gutterBottom>Applied posts ({sprintPosts.length})</Typography>
        {sprintPostsLoading && sprintPosts.length === 0 && (
          <Typography variant="caption" color="text.secondary">Loading…</Typography>
        )}
        {!sprintPostsLoading && sprintPosts.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
            No posts applied to this sprint yet. Generate drafts and accept them from the history below.
          </Typography>
        )}
        <Stack spacing={1}>
          {sprintPosts.map((sp) => (
            <UnifiedPostCell
              key={sp.id}
              testId={`sprint-post-row-${sp.id}`}
              // Concurrent Minsky refactor — Post (canonical) → PostLike adapter.
              // Cast to ContentPost is shape-compatible after Block 1: same fields.
              post={contentPostToPostLike(sp as unknown as ContentPost, {
                kind: 'sprint_post', projectId, strategyId, sprintId,
              })}
              metaChips={
                sp.lifecycleStage
                  ? <Chip size="small" label={sp.lifecycleStage} variant="outlined" />
                  : undefined
              }
              onClick={() => setEditingSprintPost(sp)}
            />
          ))}
        </Stack>
      </Box>

      {/* Sprint 15 — post generation history for this sprint */}
      <Box sx={{ mt: 4 }} data-testid="post-generation-history">
        <Typography variant="h6" gutterBottom>Post generation history</Typography>
        <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
          Past AI post-generation runs for this sprint. Accepted posts get
          <code> scheduledAt</code> computed at apply time (not at generation).
        </Typography>
        <GenerationHistoryList
          runs={sprintRuns}
          onRowClick={(run) => setPostReviewRun(run)}
          emptyHint="No post generations for this sprint yet."
        />
      </Box>

      <GenerationReviewDialog
        open={Boolean(postReviewRun)}
        onClose={() => setPostReviewRun(null)}
        projectId={projectId}
        entity="post"
        run={postReviewRun}
        titleSuffix={postReviewRun ? `${postReviewRun.generatedItemCount ?? 0} drafts` : undefined}
        onApply={async (acceptedItemIds) => {
          if (!postReviewRun) return;
          const client = new ContentPlanApiClient();
          const res = await client.applyPosts({
            projectId, authorId: authorId ?? '', generationId: postReviewRun.id, sprintId, acceptedItemIds,
          });
          if (!res.success) throw new Error((res as any).error ?? 'apply failed');
          setPostApplyToast(`Applied ${res.appliedCount} posts · skipped ${res.skippedCount} · rejected ${res.rejectedCount}`);
        }}
        renderItem={(draft) => {
          // Close-animation guard — see parallel comment in TopicDetailPanel.
          if (!postReviewRun) return null;
          const data = draft.data as unknown as ContentPost;
          const pl = contentPostToPostLike(data, {
            kind: 'content_post_draft', projectId, runId: postReviewRun.id,
          });
          return (
            <UnifiedPostCell
              post={pl}
              testId={`sprint-draft-cell-${draft.id}`}
              metaChips={
                data?.format ? <Chip size="small" variant="outlined" label={String(data.format)} /> : undefined
              }
              onClick={() => setEditingDraft(data)}
            />
          );
        }}
      />

      {/* Unified detail dialog — shared across applied sprint posts and draft
          review. `editingSprintPost` OR `editingDraft` drives which flavour
          is open; parent wires the matching write/generate callbacks. */}
      {editingSprintPost && (
        <UnifiedPostDialog
          open={!!editingSprintPost}
          onClose={() => setEditingSprintPost(null)}
          post={contentPostToPostLike(editingSprintPost as unknown as ContentPost, {
            kind: 'sprint_post', projectId, strategyId, sprintId,
          })}
          onSave={async (next: PostLike) => {
            await updatePost(editingSprintPost.id, postLikeToContentPostPatch(next) as unknown as Partial<Post>);
            return editingSprintPost.id;
          }}
          onGenerateImage={async (config) => {
            try {
              const client = new ContentPlanApiClient();
              const resp = await client.generateImage({
                projectId, userId,
                ownerKind: 'sprint_post',
                ownerId: editingSprintPost.id,
                slotKey: config.slotKey,
                experimentId: config.experimentId ?? '',
                prompt: config.prompt,
                negativePrompt: config.negativePrompt,
                aspectRatio: config.aspectRatio,
                imageSize: config.imageSize,
                style: config.style,
                model: config.model,
                referenceImages: config.referenceImages,
              });
              if (!resp.success) return { success: false, error: resp.error };
              return { success: true };
            } catch (e) {
              return { success: false, error: (e as Error).message };
            }
          }}
          onUploadReference={async (file) => {
            const sanitizedName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
            const path = `projects/${projectId}/content_strategies/${strategyId}/sprints/${sprintId}/posts/${editingSprintPost.id}/ref-images/${Date.now()}_${sanitizedName}`;
            const storage = new StorageService();
            const result = await storage.uploadImage(path, file);
            return { url: result.assetUrl.origin, mimeType: file.type || 'image/png' };
          }}
          onPromoteToExample={async () => {
            // Concurrent Minsky refactor — promote now writes to
            // authors/{aid}/platforms/{pid}/author_posts/* with kind='manual'
            // and exampleForTopicIds[] populated. Routing requires
            // authorId+platformId which are not in scope here; the dialog
            // surface that picks them lives in UnifiedPostDialog (post-Block-1).
            //
            // For backward-compat during the refactor window, the legacy
            // saveExample → useProjectPostExamples path still works against
            // the retired `post_examples/*` collection (read-only after
            // schema bump 2.0.0). After that, this no-op branch is removed
            // and UnifiedPostDialog handles the action end-to-end.
            console.warn(
              '[ContentPlansPage] onPromoteToExample is a no-op pending UnifiedPostDialog rewire (concurrent Minsky refactor).',
            );
          }}
        />
      )}

      {editingDraft && postReviewRun && (
        <UnifiedPostDialog
          open={!!editingDraft}
          onClose={() => setEditingDraft(null)}
          post={contentPostToPostLike(editingDraft, {
            kind: 'content_post_draft', projectId, runId: postReviewRun.id,
          })}
          onSave={async (_next: PostLike) => {
            // Draft edits inside the sprint review dialog route through the
            // same generation draft writer used by TopicDetailPanel. Apply
            // elsewhere; this dialog only persists pre-apply edits.
            //   → purposely no-op; consumers should open from TopicDetailPanel
            //     where `useUpdateGenerationDraft` is wired. Kept here so the
            //     cell is still clickable for preview + asset generation.
            return editingDraft.id;
          }}
          readOnly
        />
      )}

      {postApplyToast && (
        <Alert severity="success" onClose={() => setPostApplyToast(null)} sx={{ position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)', zIndex: 9999 }}>
          {postApplyToast}
        </Alert>
      )}
    </Box>
  );
};

// ─── Main page ─────────────────────────────────────────────────────

export const ContentPlansPage: React.FC<ContentPlansPageProps> = ({
  projectId, userId, strategyService, themeCatalog, onTaskStarted: handleTaskStarted,
}) => {
  const { state, update } = useDrillDownState();
  const navigate = useNavigate();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [detailTopic, setDetailTopic] = useState<ContentTopic | null>(null);
  const [detailPublication, setDetailPublication] = useState<ContentPublication | null>(null);

  // Phase 3.6 (Section 7.6.3) — open topic detail when `?topic=` arrives
  // via deep-link (e.g. backend-task panel "Open content_topic" button).
  // TopicDetailPanel only needs `topic.id` to bootstrap its own live
  // subscriptions (mirrors the calendar `pub.topicId` pattern at line ~1284),
  // so we stub the topic with just the id.
  React.useEffect(() => {
    if (state.topicId && (!detailTopic || detailTopic.id !== state.topicId)) {
      setDetailTopic({ id: state.topicId } as ContentTopic);
    }
    // When the user closes the dialog and topicId is still in the URL,
    // we DON'T re-open — the close handler clears state.topicId via update().
  }, [state.topicId, detailTopic]);
  // When a TaskErrorResolver CTA asks us to retry Stage B, reopen the
  // strategy dialog in one-shot mode (auto-fill + auto-assign together).
  const [forceAutoFill, setForceAutoFill] = useState(false);
  // Phase A creator-centric refactor — author onboarding flow
  const [onboardingOpen, setOnboardingOpen] = useState(false);
  const [onboardingExistingAuthorId, setOnboardingExistingAuthorId] = useState<string | undefined>(undefined);
  const [cloneTarget, setCloneTarget] = useState<AuthorDoc | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<AuthorDoc | null>(null);
  const [deletePreview, setDeletePreview] = useState<CascadeDeleteCounts | null>(null);
  const [deletePreviewLoading, setDeletePreviewLoading] = useState(false);
  const [deletingAuthor, setDeletingAuthor] = useState(false);
  // Phase 1 redesign — toast queue (lightweight implementation; Phase 3 will
  // promote to a context-provided hook). Each toast can carry an action button
  // that the user clicks to opt into navigation. The toast itself never
  // navigates.
  const [toast, setToast] = useState<{
    message: string;
    severity: 'success' | 'info' | 'warning' | 'error';
    action?: { label: string; testId?: string; onClick: () => void };
  } | null>(null);
  // M2 wizard (LT1) — persistent long-task banner. Page-level state so it
  // survives dialog close and tab switches; sessionStorage so it survives a
  // reload mid-task. The wizard strip renders the banner.
  const [activeTask, setActiveTask] = useState<{ taskId: string; label: string } | null>(() => {
    try {
      const raw = sessionStorage.getItem(`cp:wizard:task:${projectId}`);
      return raw ? (JSON.parse(raw) as { taskId: string; label: string }) : null;
    } catch {
      return null;
    }
  });
  const [wizardExpandNonce, setWizardExpandNonce] = useState(0);
  // DE5 — overview author-picker dialog (opened from overview alerts).
  const [overviewPickerOpen, setOverviewPickerOpen] = useState(false);

  const handleWizardTaskLaunched = React.useCallback((taskId: string, label: string) => {
    const next = { taskId, label };
    setActiveTask(next);
    try {
      sessionStorage.setItem(`cp:wizard:task:${projectId}`, JSON.stringify(next));
    } catch {
      // Storage can be disabled; the banner still lives in page state.
    }
    // Toast stays a SECONDARY courier with an opt-in CTA (no auto-navigation);
    // the persistent banner above the tabs is the primary progress surface.
    setToast({
      severity: 'info',
      message: `${label} started.`,
      action: {
        label: 'View progress',
        testId: CONTENT_PLAN.TOAST_VIEW_PROGRESS,
        onClick: () => setWizardExpandNonce((nonce) => nonce + 1),
      },
    });
  }, [projectId]);

  const handleDismissTask = React.useCallback(() => {
    setActiveTask(null);
    try {
      sessionStorage.removeItem(`cp:wizard:task:${projectId}`);
    } catch {
      // Ignore storage errors.
    }
  }, [projectId]);
  // Phase 1 redesign — auto-jump for backlog-generation CTA from the
  // post-strategy toast. Exposed so the top-tab handler can route the user
  // to Backlog without a programmatic tab switch (it's user-initiated).
  // Audience options for onboarding dialog. Minimal MVP: empty array; Phase B
  // wires in useProjectAudiences (module boundary). Users can create author
  // even with no audiences — backend validates non-empty at submit.
  const audienceOptions: AudienceOption[] = useMemo(() => [], []);
  const authorWorkspace = useAuthorWorkspace(projectId);
  const {
    authors,
    author,
    authorId,
    requestedAuthorId,
    isOverview,
    loading: authorLoading,
    navigateToAuthor,
    setDefaultAuthor,
  } = authorWorkspace;
  const workspaceAuthorId = isOverview ? null : authorId;
  const invalidAuthorId = requestedAuthorId && requestedAuthorId !== 'all' && !authorLoading && !author
    ? requestedAuthorId
    : null;
  const waitingForRequestedAuthor = Boolean(requestedAuthorId && requestedAuthorId !== 'all' && authorLoading && !author);

  React.useEffect(() => {
    if (!deleteTarget?.id) {
      setDeletePreview(null);
      setDeletePreviewLoading(false);
      return;
    }
    let active = true;
    setDeletePreviewLoading(true);
    previewCascadeDeleteSelfAuthor(projectId, deleteTarget.id)
      .then((counts) => {
        if (active) setDeletePreview(counts);
      })
      .catch((err) => {
        if (active) {
          setDeletePreview(null);
          setToast({ severity: 'warning', message: `Could not load delete preview: ${(err as Error).message}` });
        }
      })
      .finally(() => {
        if (active) setDeletePreviewLoading(false);
      });
    return () => {
      active = false;
    };
  }, [deleteTarget?.id, projectId]);

  const handleDeleteAuthor = async () => {
    if (!deleteTarget?.id || deletingAuthor) return;
    if (deleteTarget.isDefault) {
      setToast({
        severity: 'warning',
        message: 'Set another default baseline before deleting this author.',
      });
      return;
    }
    setDeletingAuthor(true);
    try {
      const counts = await cascadeDeleteSelfAuthor(projectId, deleteTarget.id);
      const fallback = authors.find((candidate) => candidate.id && candidate.id !== deleteTarget.id);
      setDeleteTarget(null);
      navigateToAuthor(fallback?.id ?? 'all', { preserveTab: true });
      setToast({
        severity: 'success',
        message: `Author workspace deleted: ${counts.contentStrategies} strategies, ${counts.topics} topics, ${counts.contentPosts} posts.`,
      });
    } catch (err) {
      setToast({ severity: 'error', message: err instanceof Error ? err.message : String(err) });
    } finally {
      setDeletingAuthor(false);
    }
  };
  // Concurrent Minsky refactor — old `?cpTab=post-examples` and
  // `?cpTab=creators` URLs redirect to `cpTab=creator`. Creator Research is
  // hidden from the primary setup flow until it is reliable. `?cpTab=scheduled`
  // still redirects to `backlog` (old bookmarks); the Scheduled research UI was
  // removed.
  const rawSubTab = (state.subTab ?? 'backlog') as string;
  const normalizedSubTab =
    rawSubTab === 'post-examples' || rawSubTab === 'creators' ? 'creator'
    : rawSubTab === 'scheduled' ? 'backlog'
    : rawSubTab;
  const subTab: SubTabId = isKnownSubTab(normalizedSubTab) ? normalizedSubTab : 'backlog';
  React.useEffect(() => {
    if (state.subTab && rawSubTab !== subTab) {
      update({ ...state, subTab: subTab as DrillDownState['subTab'] });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawSubTab, subTab]);

  // Tab lookup — derive top-tab from the sub-tab via SUB_TO_TOP. Done at the
  // component top-level so both tab-strip rendering and the sub-tab strip
  // (which lives inside the content Paper) share the same `currentTop` value
  // without duplicate map lookups.
  const topTabId: TopTabId = SUB_TO_TOP[subTab as SubTabId] ?? FIRST_TOP_TAB_ID;
  const currentTop = TOP_TAB_CONFIG.find((t) => t.id === topTabId)!;

  React.useEffect(() => {
    try {
      sessionStorage.setItem(`cp:last-sub:${topTabId}`, subTab);
    } catch {
      // Storage can be disabled in private/test contexts; tab routing remains URL-driven.
    }
  }, [subTab, topTabId]);

  // Listen for resolution events emitted by <TaskErrorResolver>.
  //
  // Phase 1 redesign rule: the listener MUST NOT mutate `subTab`. It opens
  // the relevant dialog stacked over whatever tab the user is currently on.
  // This way an error elsewhere (e.g. fired while user is reviewing Calendar)
  // never yanks them out of context.
  // G5 (AC-GP-029): the SAME handling is registered per task type, so the
  // resolver only offers a button for a content-plan task while this page is
  // mounted to act on it. The window listener stays: it is the channel the
  // atomic e2e specs dispatch on, and the resolver's fallback when a handler
  // unregisters between render and click.
  React.useEffect(() => {
    // Fix round 1 (I1): the handler ANSWERS — `false` means this page could not act
    // on that code, and the resolver says so instead of leaving the press silent.
    const resolve = (detail: { code?: string; action?: string }): boolean => {
      const { code, action } = detail ?? {};
      if (code === 'INSUFFICIENT_BACKLOG' && action === 'retry-with-autofill') {
        setForceAutoFill(true);
        setDialogOpen(true);
      } else if (code === 'INSUFFICIENT_BACKLOG' && action === 'open-backlog') {
        // Surface a toast pointing the user to Backlog. They click to navigate.
        setToast({
          severity: 'info',
          message: 'Backlog is short on topics. Generate more to retry the strategy.',
          action: {
            label: 'Open Backlog',
            testId: 'toast-action-open-backlog',
            onClick: () => update({ subTab: 'backlog' }),
          },
        });
      } else if (code === 'EMPTY_SOURCE_DATA' && action === 'reopen-dialog') {
        // Re-open the dialog the error came from. No tab change — the user
        // may not even be on the strategies tab; the dialog opens stacked.
        setDialogOpen(true);
      } else {
        return false;
      }
      return true;
    };
    const handler = (ev: Event) => resolve(((ev as CustomEvent).detail ?? {}) as { code?: string; action?: string });
    window.addEventListener('task-error:resolve', handler);
    const unregister = CONTENT_PLAN_RESOLVABLE_TASK_TYPES
      .map((taskType) => registerTaskResolveHandler(taskType, resolve));
    return () => {
      window.removeEventListener('task-error:resolve', handler);
      for (const off of unregister) off();
    };
  }, [update]);

  if (!requestedAuthorId || waitingForRequestedAuthor || invalidAuthorId || (authors.length === 0 && !authorLoading)) {
    return (
      <Box data-testid={CONTENT_PLAN.PAGE_ROOT}>
        <AuthorPickerScreen
          authors={authors}
          loading={authorLoading}
          invalidAuthorId={invalidAuthorId}
          onSelect={(id) => navigateToAuthor(id, { preserveTab: true, preserveContext: true })}
          onOverview={() => navigateToAuthor('all', { preserveTab: true, preserveContext: true })}
          onCreate={() => {
            setOnboardingExistingAuthorId(undefined);
            setOnboardingOpen(true);
          }}
        />
        <AuthorOnboardingDialog
          open={onboardingOpen}
          projectId={projectId}
          userId={userId}
          existingAuthorId={onboardingExistingAuthorId}
          audiences={audienceOptions}
          defaultLocale="en-US"
          createNewSelfAuthor={authors.length > 0}
          onClose={() => {
            setOnboardingOpen(false);
            setOnboardingExistingAuthorId(undefined);
          }}
          onComplete={(newAuthorId) => {
            setOnboardingOpen(false);
            setOnboardingExistingAuthorId(undefined);
            if (newAuthorId) navigateToAuthor(newAuthorId, { preserveTab: true, preserveContext: true });
          }}
        />
      </Box>
    );
  }

  return (
    <Box
      data-testid={CONTENT_PLAN.PAGE_ROOT}
      sx={{
        minHeight: 'calc(100vh - 24px)',
        height: subTab === 'backlog' ? '100vh' : undefined,
        display: 'flex',
        flexDirection: 'column',
        overflow: subTab === 'backlog' ? 'hidden' : undefined,
      }}
    >
      {!isOverview && (
        <AuthorOnboardingStatusBanner
          projectId={projectId}
          onSetupClick={() => setOnboardingOpen(true)}
        />
      )}

      {toast && (
        <Alert
          severity={toast.severity}
          data-testid="toast-content-plan"
          onClose={() => setToast(null)}
          action={
            toast.action ? (
              <Button
                size="small"
                color="inherit"
                data-testid={toast.action.testId}
                onClick={() => {
                  toast.action!.onClick();
                  setToast(null);
                }}
              >
                {toast.action.label}
              </Button>
            ) : undefined
          }
          sx={{ mb: 2 }}
        >
          {toast.message}
        </Alert>
      )}

      <AuthorOnboardingDialog
        open={onboardingOpen}
        projectId={projectId}
        userId={userId}
        existingAuthorId={onboardingExistingAuthorId}
        audiences={audienceOptions}
        defaultLocale="en-US"
        createNewSelfAuthor={authors.length > 0 && !onboardingExistingAuthorId}
        onClose={() => {
          setOnboardingOpen(false);
          setOnboardingExistingAuthorId(undefined);
        }}
        onComplete={(newAuthorId) => {
          setOnboardingOpen(false);
          setOnboardingExistingAuthorId(undefined);
          if (newAuthorId) navigateToAuthor(newAuthorId, { preserveTab: true });
          // Phase 1 redesign: do NOT auto-navigate. The user is already on
          // the Creator sub-tab (only place to launch onboarding from).
          // Surface a toast for confirmation; user stays put.
          setToast({
            severity: 'success',
            message: 'Author profile saved · post examples imported · draft pillars created.',
          });
        }}
      />
      <CloneAuthorDialog
        open={Boolean(cloneTarget)}
        projectId={projectId}
        sourceAuthor={cloneTarget}
        onClose={() => setCloneTarget(null)}
        onCloned={(newAuthorId) => {
          setCloneTarget(null);
          navigateToAuthor(newAuthorId, { preserveTab: true });
          setToast({ severity: 'success', message: 'Author cloned.' });
        }}
      />
      <SDialog
        open={Boolean(deleteTarget)}
        onClose={() => {
          if (!deletingAuthor) setDeleteTarget(null);
        }}
        title="Delete author workspace"
        maxWidth="sm"
        fullWidth
        data-testid="delete-author-dialog"
        primaryAction={{
          label: 'Delete',
          color: 'error',
          onClick: handleDeleteAuthor,
          loading: deletingAuthor,
          disabled: deletingAuthor || !deleteTarget?.id || Boolean(deleteTarget?.isDefault),
          'data-testid': 'delete-author-confirm',
        }}
        secondaryAction={{
          label: 'Cancel',
          onClick: () => setDeleteTarget(null),
          disabled: deletingAuthor,
        }}
      >
        <Stack spacing={2}>
          {deleteTarget?.isDefault && (
            <Alert severity="warning">Set another default baseline before deleting this author.</Alert>
          )}
          <Typography variant="body2" color="text.secondary">
            This deletes the author profile, platforms, voice, design settings, pillars, strategies, topics, posts, research runs, generation runs, and tasks owned by this author.
          </Typography>
          <Box data-testid="delete-author-preview">
            {deletePreviewLoading ? (
              <Typography variant="body2" color="text.secondary">Counting owned records…</Typography>
            ) : deletePreview ? (
              <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
                <Chip size="small" label={`${deletePreview.platforms} platforms`} />
                <Chip size="small" label={`${deletePreview.topicPillars} pillars`} />
                <Chip size="small" label={`${deletePreview.contentStrategies} strategies`} />
                <Chip size="small" label={`${deletePreview.topics} topics`} />
                <Chip size="small" label={`${deletePreview.contentPosts} posts`} />
                <Chip size="small" label={`${deletePreview.researchRuns} research runs`} />
                <Chip size="small" label={`${deletePreview.topicGenerationRuns + deletePreview.postGenerationRuns} generation runs`} />
                <Chip size="small" label={`${deletePreview.backendTasks} tasks`} />
              </Stack>
            ) : (
              <Typography variant="body2" color="text.secondary">No count preview available.</Typography>
            )}
          </Box>
          <Typography variant="body2" fontWeight={600}>
            {deleteTarget?.displayName ?? deleteTarget?.id}
          </Typography>
        </Stack>
      </SDialog>

      {/* M2 wizard — collapsible guided-flow layer ABOVE the tab shell (D7).
          It annotates and links; ONLY explicit user clicks navigate (D8). */}
      <ContentPlanWizardStrip
        projectId={projectId}
        userId={userId}
        authorId={workspaceAuthorId}
        isOverview={isOverview}
        strategyService={strategyService}
        themeCatalog={themeCatalog}
        onOpenSubTab={(sub: WizardSubTabId) => update({ ...state, subTab: sub as DrillDownState['subTab'] })}
        onOpenOnboarding={() => setOnboardingOpen(true)}
        onOpenCreateStrategy={() => setDialogOpen(true)}
        activeTask={activeTask}
        onDismissTask={handleDismissTask}
        onTaskLaunched={handleWizardTaskLaunched}
        expandNonce={wizardExpandNonce}
      />

      {/* DE5 — author picker opened in place from overview alerts. */}
      <SDialog
        open={overviewPickerOpen}
        onClose={() => setOverviewPickerOpen(false)}
        title="Pick an author workspace"
        maxWidth="md"
        fullWidth
        data-testid={CONTENT_PLAN.OVERVIEW_AUTHOR_PICKER_DIALOG}
      >
        <AuthorPickerScreen
          authors={authors}
          loading={authorLoading}
          onSelect={(id) => {
            setOverviewPickerOpen(false);
            navigateToAuthor(id, { preserveTab: true, preserveContext: true });
          }}
          onCreate={() => {
            setOverviewPickerOpen(false);
            setOnboardingExistingAuthorId(undefined);
            setOnboardingOpen(true);
          }}
        />
      </SDialog>

      <Box
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          flexShrink: 0,
        }}
      >
        <Tabs
          value={subTab}
          onChange={(_, nextId: SubTabId) => update({ ...state, subTab: nextId as DrillDownState['subTab'] })}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            minHeight: 'auto',
            pt: '1px',
            flex: 1,
            minWidth: 0,
          }}
          data-testid="content-plan-sub-tabs"
        >
          {currentTop.subTabs.map((sub) => {
            const Icon = sub.icon;
            return (
              <Tab
                key={sub.id}
                value={sub.id}
                label={sub.label}
                icon={<Icon />}
                iconPosition="start"
                sx={{ pl: '0', pr: '32px', py: '12px', minHeight: 'auto' }}
                data-testid={sub.testId}
              />
            );
          })}
        </Tabs>

        <Stack
          direction="row"
          alignItems="center"
          justifyContent="flex-end"
          spacing={1}
          sx={{ flexShrink: 0 }}
        >
          {isOverview && <Chip size="small" color="info" variant="outlined" label="Read-only overview" />}
          <AuthorSelector
            authors={authors}
            activeAuthorId={workspaceAuthorId}
            isOverview={isOverview}
            canManageAuthors={topTabId === 'creator-setup' && !isOverview}
            onSelect={(id) => navigateToAuthor(id, { preserveTab: true })}
            onSetDefault={setDefaultAuthor}
            onClone={setCloneTarget}
            onDelete={setDeleteTarget}
          />
        </Stack>
      </Box>

      <Paper
        elevation={0}
        sx={{
          p: { xs: 1, sm: 2, md: 3 },
          mt: 3,
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          overflow: subTab === 'backlog' ? 'hidden' : undefined,
        }}
      >

        {subTab === 'creator' && (
          isOverview ? (
            <Alert
              severity="info"
              action={
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => setOverviewPickerOpen(true)}
                  data-testid={CONTENT_PLAN.OVERVIEW_PICK_AUTHOR_CTA}
                >
                  Pick author
                </Button>
              }
            >
              Pick a single author to edit creator settings.
            </Alert>
          ) : (
            <CreatorTab
              projectId={projectId}
              userId={userId}
              onOnboardClick={() => setOnboardingOpen(true)}
              onAddAuthorClick={() => {
                setOnboardingExistingAuthorId(undefined);
                setOnboardingOpen(true);
              }}
              onRedoClick={(authorId) => {
                setOnboardingExistingAuthorId(authorId);
                setOnboardingOpen(true);
              }}
              onTaskStarted={(taskId, label) => {
                handleTaskStarted?.(taskId, label);
                // LT1 — surface creator-tab tasks in the persistent banner too.
                handleWizardTaskLaunched(taskId, label);
              }}
            />
          )
        )}

        {subTab === 'platforms' && (
          isOverview ? (
            <Alert
              severity="info"
              action={
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => setOverviewPickerOpen(true)}
                  data-testid={CONTENT_PLAN.OVERVIEW_PICK_AUTHOR_CTA}
                >
                  Pick author
                </Button>
              }
            >
              Pick a single author to edit platform settings.
            </Alert>
          ) : (
            <PlatformsSection
              projectId={projectId}
              authorId={workspaceAuthorId}
              userId={userId}
              defaultLocale={author?.defaultLocale}
              variant="overview"
            />
          )
        )}

        {subTab === 'backlog' && (
          <Box sx={{ flex: 1, minHeight: 0, height: '100%' }}>
            {isOverview ? (
              <Alert
                severity="info"
                action={
                  <Button
                    color="inherit"
                    size="small"
                    onClick={() => setOverviewPickerOpen(true)}
                    data-testid={CONTENT_PLAN.OVERVIEW_PICK_AUTHOR_CTA}
                  >
                    Pick author
                  </Button>
                }
              >
                Pick a single author to edit the backlog.
              </Alert>
            ) : (
            <BacklogTab
              projectId={projectId}
              userId={userId}
              authorId={workspaceAuthorId}
              strategyService={strategyService}
              themeCatalog={themeCatalog}
              onOpenTopic={setDetailTopic}
            />
            )}
          </Box>
        )}

        {subTab === 'themes' && (
          <ThemesTab
            projectId={projectId}
            userId={userId}
            strategyService={strategyService}
          />
        )}

        {/* Hidden/deferred sub-tabs:
            - `post-examples` redirects to `creator`.
            - `creators` (tracked Creator Research) redirects to `creator`.
            - `scheduled` redirects to `backlog` (the Scheduled research UI was removed).
            The Creator Research components are intentionally not rendered in v1 primary navigation. */}

        {subTab === 'calendar' && (
          <ContentCalendarPanel
            projectId={projectId}
            userId={userId}
            authorId={isOverview ? 'all' : workspaceAuthorId}
            initialStrategyId={state.strategyId}
            // DE2 — empty-calendar CTAs. Both are explicit user clicks.
            onOpenStrategiesSubTab={() => update({ ...state, subTab: 'strategies' })}
            onCreateStrategy={isOverview ? undefined : () => setDialogOpen(true)}
            onOpenPublication={(pub) => {
              // Phase 1 redesign: clicking a chip never changes the active tab.
              // It opens the relevant detail in a stacked panel/dialog while
              // the user remains on Calendar. Topic chips → TopicDetailPanel
              // drawer (pre-fetched). Post-attached chips fall through to the
              // unified post dialog (handled inside ContentCalendarPanel).
              if (pub.topicId) {
                setDetailPublication(pub);
                setDetailTopic({ id: pub.topicId } as ContentTopic);
              }
            }}
          />
        )}

        {subTab === 'engagement' && (
          <SocialEngagementPanel projectId={projectId} />
        )}

        {subTab === 'strategies' && (
          <StrategiesTable
            projectId={projectId}
            authorId={isOverview ? 'all' : workspaceAuthorId}
            readOnly={isOverview}
            onOpenStrategy={(id) => update({ ...state, strategyId: id })}
            onCreateClick={() => setDialogOpen(true)}
            onDeleted={(id) => {
              if (state.strategyId === id) {
                update({ ...state, strategyId: undefined, sprintId: undefined });
              }
            }}
          />
        )}
      </Paper>

      {/* Strategy detail dialog (Level 1) — renders via MUI Portal; lives outside
          the content Paper. Open state is fully derived from URL drill-keys; a
          stale URL (?strategyId=X but user on a different sub-tab) is harmless
          because the dialogs stay closed unless their gating expression matches. */}
      <StrategyDetailsDialog
        open={Boolean(state.strategyId) && subTab === 'strategies'}
        onClose={() => update({ ...state, strategyId: undefined, sprintId: undefined })}
        projectId={projectId}
        userId={userId}
        authorId={workspaceAuthorId}
        strategyId={state.strategyId}
        onOpenSprint={(id) => update({ ...state, sprintId: id })}
        onTaskLaunched={handleWizardTaskLaunched}
      />

      {/* Sprint detail dialog (Level 2) — stacks above the strategy dialog.
          Migrated from BaseDialog → SDialog (new ui-components standard). */}
      <SDialog
        open={Boolean(state.strategyId && state.sprintId) && subTab === 'strategies'}
        onClose={() => update({ ...state, sprintId: undefined })}
        title="Sprint detail"
        maxWidth="lg"
        fullWidth
        data-testid={CONTENT_PLAN.SPRINT_DETAIL_DIALOG}
      >
        {state.strategyId && state.sprintId && subTab === 'strategies' && (
          <SprintDetail
            projectId={projectId}
            userId={userId}
            authorId={workspaceAuthorId}
            strategyId={state.strategyId}
            sprintId={state.sprintId}
            onOpenTopic={setDetailTopic}
            onTaskLaunched={handleWizardTaskLaunched}
          />
        )}
      </SDialog>

      <CreateContentStrategyDialog
        open={dialogOpen}
        onClose={() => { setDialogOpen(false); setForceAutoFill(false); }}
        projectId={projectId}
        userId={userId}
        authorId={workspaceAuthorId}
        strategyService={strategyService}
        initialAutoFillBacklog={forceAutoFill}
        onSuccess={({ taskId, strategyId }) => {
          // Phase 1 redesign: do NOT auto-navigate. LT1: for a real backend
          // task, the persistent wizard banner becomes the primary surface
          // (handleWizardTaskLaunched also fires the opt-in toast). Blank
          // strategies keep the immediate "Open strategy" toast.
          if (taskId && !taskId.startsWith('blank_')) {
            handleWizardTaskLaunched(taskId, 'Create strategy');
            return;
          }
          setToast({
            severity: 'success',
            message: 'Strategy created.',
            action: {
              label: 'Open strategy',
              testId: 'toast-action-open-strategy',
              onClick: () => update({ ...state, subTab: 'strategies', strategyId, sprintId: undefined }),
            },
          });
        }}
      />

      <TopicDetailPanel
        open={Boolean(detailTopic)}
        onClose={() => {
          setDetailTopic(null);
          setDetailPublication(null);
          // Phase 3.6 (Section 7.6.3) — when the dialog was opened via
          // deep-link (`?topic=...`), strip the param on close so the
          // useEffect above doesn't re-open it on re-render.
          if (state.topicId) update({ ...state, topicId: undefined });
        }}
        topic={detailTopic}
        projectId={projectId}
        userId={userId}
        authorId={detailPublication?.authorId ?? workspaceAuthorId ?? undefined}
        strategyId={detailPublication?.strategyId ?? state.strategyId}
        sprintId={detailPublication?.sprintId}
        publicationId={detailPublication?.id}
        publicationPlatform={detailPublication?.platform}
        publicationAspectKey={detailPublication?.aspectKey ?? null}
        publicationSlotAssignment={detailPublication?.slotAssignment ?? null}
      />
    </Box>
  );
};

export default ContentPlansPage;
